import { CarrierRoles, Roles } from './constants/roles';

export function canEditDecision(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.CHIEF_UNDERWRITER,
        Roles.ADMIN,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
}

export function canDeleteUpload(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [Roles.CHIEF_UNDERWRITER, Roles.ADMIN, Roles.CHIEF_CASEMANAGER].includes(
        r,
      ),
    )
  );
}

export function isAdmin(roles: string[]) {
  return roles && roles.some(r => [Roles.ADMIN, Roles.WORKBENCH_ADMIN].includes(r));
}

export function isManager(roles: string[]) {
  return roles && roles.some(r => [Roles.UW_UNDERWRITING_MANAGER].includes(r));
}

export function canDoUWxAction(roles: string[]) {
  return roles && roles.some(r => [Roles.UWX_TEAM, Roles.ADMIN].includes(r));
}

export function isReadOnlyUser(roles: string[]) {
  return roles && roles.some(r => r === Roles.READ_ONLY);
}

export function getPaForLga(debits: number): number {
  const paValue = debits + 100;
  if (paValue >= 275 && paValue <= 350) {
    return 350;
  }
  return paValue;
}

/**
 * Helper to determine what view to display given the user roles
 *
 * @public
 *
 * @return {boolean} - a boolean indicating whether to show master view (if so true)
 */
export function shouldShowMasterView(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.ADMIN,
        Roles.CHIEF_CASEMANAGER,
        Roles.CHIEF_UNDERWRITER,
        ...CarrierRoles,
      ].includes(r),
    )
  );
}

export function isUnderwriter(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
}

export function isUnderwriterLiaison(roles: string[]) {
  return roles && roles.includes(Roles.UW_LIAISON);
}

export function canViewHipaa(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.ADMIN,
        Roles.CASEMANAGER,
        Roles.CHIEF_CASEMANAGER,
        Roles.UWX_TEAM,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.CHIEF_UNDERWRITER,
        Roles.UNDERWRITER,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
}

export function canSeeReleaseTransferAuthorization(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.ADMIN,
        Roles.UWX_TEAM,
        Roles.CHIEF_UNDERWRITER,
        Roles.CHIEF_CASEMANAGER,
        Roles.UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
}

export function canCreateSpecialAuthorizationTask(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.ADMIN,
        Roles.UWX_TEAM,
        Roles.CHIEF_UNDERWRITER,
        Roles.CHIEF_CASEMANAGER,
        Roles.CASEMANAGER,
      ].includes(r),
    )
  );
}

export function canCreatePlanFTask(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.ADMIN,
        Roles.CHIEF_UNDERWRITER,
        Roles.CHIEF_CASEMANAGER,
        Roles.CASEMANAGER,
        Roles.PRODUCTION_UNDERWRITER,
      ].includes(r),
    )
  );
}

export function canOrderInfoFromCustomer(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UWX_TEAM,
        Roles.ADMIN,
        Roles.CASEMANAGER,
        Roles.CHIEF_CASEMANAGER,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
}

export function canOrderAdditionalDocuments(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UWX_TEAM,
        Roles.ADMIN,
        Roles.CASEMANAGER,
        Roles.CHIEF_CASEMANAGER,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
}

export function canReinstateApplication(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.PRODUCTION_UNDERWRITER,
        Roles.ADMIN,
        Roles.CHIEF_UNDERWRITER,
        Roles.UW_LIAISON,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
        Roles.CASEMANAGER,
      ].includes(r),
    )
  );
}

export function canViewPIIData(roles: string[]) {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.CHIEF_UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UNDERWRITER,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.ADMIN,
        Roles.CASEMANAGER,
        Roles.UW_LIAISON,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
        ...CarrierRoles,
      ].includes(r),
    )
  );
}

export const isUserEligibleForSmartCaseAssignment = (
  roles: string[],
): boolean => {
  for (const role of roles) {
    switch (role) {
      case Roles.UNDERWRITER:
      case Roles.CHIEF_UNDERWRITER:
      case Roles.PRODUCTION_UNDERWRITER:
      case Roles.UW_FLEX_UNDERWRITER:
      case Roles.UW_LIAISON:
      case Roles.UW_JUNIOR_UNDERWRITER:
      case Roles.UW_UNDERWRITING_MANAGER:
        return true;
    }
  }
  return false;
};

export const isUserEligibleForOverviewPage = (roles: string[]): boolean => {
  return (
    roles &&
    roles.some(r =>
      [
        Roles.ADMIN,
        Roles.CHIEF_UNDERWRITER,
        Roles.PRODUCTION_UNDERWRITER,
        Roles.UW_FLEX_UNDERWRITER,
        Roles.UW_LIAISON,
        Roles.UNDERWRITER,
        Roles.UW_JUNIOR_UNDERWRITER,
        Roles.UW_UNDERWRITING_MANAGER,
      ].includes(r),
    )
  );
};

export const canSeeActions = (roles: string[]): boolean => {
  return (
    roles &&
    roles.some(r =>
      [Roles.ADMIN, Roles.CHIEF_UNDERWRITER, Roles.CHIEF_CASEMANAGER].includes(
        r,
      ),
    )
  );
};

export const canInitReconsideration = (roles: string[]): boolean => {
  return (
    roles && roles.some(r => [Roles.ADMIN, Roles.CHIEF_UNDERWRITER].includes(r))
  );
};

export const isUserEligibleForQaListPage = (roles: string[]): boolean => {
  return (
    roles &&
    roles.some(r => [Roles.ADMIN, Roles.UW_STRATEGY_REVIEWER].includes(r))
  );
};
